import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Facebook, Instagram, Linkedin } from "lucide-react";

const footerLinks = {
  services: [
    { name: "Cours Particuliers", href: "#services" },
    { name: "Cours Collectifs", href: "#services" },
    { name: "Cours en Visio", href: "#services" },
    { name: "Stages de Révisions", href: "#services" },
    { name: "English Class (dès 3 ans)", href: "#services" },
    { name: "Scolarité Atypique", href: "#services" }
  ],
  company: [
    { name: "À propos", href: "#apropos" },
    { name: "Nos centres", href: "#centres" },
    { name: "Contact", href: "#contact" }
  ]
};

const socialLinks = [
  { icon: Facebook, href: "https://www.facebook.com", label: "Facebook" },
  { icon: Instagram, href: "https://www.instagram.com/celinepotentielplus/", label: "Instagram" },
  { icon: Linkedin, href: "https://www.linkedin.com", label: "LinkedIn" }
];

export const Footer = ({ onOpenLegal }) => {
  return (
    <footer data-testid="footer" className="relative bg-blue-600 pt-14 md:pt-16 pb-28 md:pb-8 overflow-hidden">
      {/* Background Brand */}
      <div className="absolute bottom-0 left-0 right-0 overflow-hidden pointer-events-none">
        <div className="footer-brand font-extrabold tracking-tighter text-center select-none">
          POTENTIEL+
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10 mb-10 md:mb-12">
          {/* Brand Column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-1"
          >
            <a href="#accueil" className="inline-block mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_site-creator-1560/artifacts/bqfdt7aq_02_logo_fond_transparent.png"
                alt="Potentiel+ Logo"
                className="h-14 w-auto object-contain bg-white rounded-lg p-2"
              />
            </a>
            <p className="text-white/80 mb-6 text-sm leading-relaxed">
              Votre partenaire de confiance pour l'accompagnement scolaire 
              de vos enfants dans les Yvelines.
            </p>
            
            {/* Social Links */}
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  aria-label={social.label}
                  data-testid={`footer-social-${social.label.toLowerCase()}`}
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-orange-500 flex items-center justify-center transition-colors group"
                >
                  <social.icon className="w-5 h-5 text-white/80 group-hover:text-white transition-colors" />
                </a>
              ))}
            </div>
          </motion.div>

          {/* Services Column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h4 className="text-white font-semibold mb-5">Nos Services</h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-white/80 hover:text-orange-400 text-sm transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Company Column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h4 className="text-white font-semibold mb-5">L'entreprise</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-white/80 hover:text-orange-400 text-sm transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
              <li>
                <button
                  onClick={onOpenLegal}
                  className="text-white/80 hover:text-orange-400 text-sm transition-colors text-left"
                >
                  Mentions légales
                </button>
              </li>
              <li>
                <button
                  onClick={onOpenLegal}
                  className="text-white/80 hover:text-orange-400 text-sm transition-colors text-left"
                >
                  Politique de confidentialité
                </button>
              </li>
            </ul>
          </motion.div>

          {/* Contact Column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h4 className="text-white font-semibold mb-5">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
                <span className="text-white/80 text-sm">
                  23 rue de Goupillières<br />
                  78910 FLEXANVILLE
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-orange-400 flex-shrink-0" />
                <a href="tel:+33664446823" className="text-white/80 hover:text-orange-400 text-sm transition-colors">
                  06 64 44 68 23
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-orange-400 flex-shrink-0" />
                <a href="mailto:contact@potentielplus.net" className="text-white/80 hover:text-orange-400 text-sm transition-colors">
                  contact@potentielplus.net
                </a>
              </li>
            </ul>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/20 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/60 text-sm">
              © {new Date().getFullYear()} POTENTIEL+. Tous droits réservés.
            </p>
            <div className="flex gap-6">
              <button 
                onClick={onOpenLegal}
                className="text-white/60 hover:text-white/90 text-sm transition-colors"
              >
                Mentions légales
              </button>
              <button 
                onClick={onOpenLegal}
                className="text-white/60 hover:text-white/90 text-sm transition-colors"
              >
                Confidentialité
              </button>
              <button 
                onClick={onOpenLegal}
                className="text-white/60 hover:text-white/90 text-sm transition-colors"
              >
                CGV
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
